import React, { useMemo } from "react";
import { Place } from "react-tooltip";
import { styled } from "twin.macro";

import { Icon } from "src/shared/components";
import { useDeviceSize } from "src/shared/hooks";
import PerkStatusTooltip from "src/views/sponsor/perks/components/PerkStatusTooltip";
import {
  PERK_STATUS_TO_ICONS,
  PERK_STATUS_TO_LABELS,
  PERK_STATUS_ORDER,
} from "src/views/sponsor/perks/types";

const COLUMN_COUNT_WIDE = 2;

const Legend: React.FC = () => {
  const isCompactLegend = useDeviceSize("medium");
  const isTabletOrSmaller = useDeviceSize("tablet");

  const legendColumns = useMemo(() => {
    const columnCount = isCompactLegend ? 1 : COLUMN_COUNT_WIDE;
    const itemsPerColumn = Math.ceil(PERK_STATUS_ORDER.length / columnCount);

    return Array.from({ length: columnCount }, (_, colIdx) =>
      PERK_STATUS_ORDER.slice(
        colIdx * itemsPerColumn,
        (colIdx + 1) * itemsPerColumn
      )
    );
  }, [isCompactLegend]);

  const getTooltipDirection = (colIdx: number): Place => {
    if (isCompactLegend) {
      return isTabletOrSmaller ? "right" : "left";
    }

    return colIdx === 1 ? "right" : "left";
  };

  return (
    <Container>
      <Title>LEGEND</Title>
      <LegendGrid $isCompact={isCompactLegend}>
        {legendColumns.map((columnStatuses, colIdx) => (
          <LegendColumn key={colIdx}>
            {columnStatuses.map((status) => (
              <LegendItem key={status}>
                <PerkStatusTooltip
                  id={`legend-perk-status-tooltip-${status}`}
                  status={status}
                  preferredDirection={getTooltipDirection(colIdx)}
                >
                  <IconContainer>
                    <Icon size="18px" name={PERK_STATUS_TO_ICONS[status]} />
                  </IconContainer>
                  {PERK_STATUS_TO_LABELS[status]}
                </PerkStatusTooltip>
              </LegendItem>
            ))}
          </LegendColumn>
        ))}
      </LegendGrid>
    </Container>
  );
};

const Container = styled.div`
  padding: 0 45px 25px 45px;
  width: 100%;
  flex-shrink: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const Title = styled.div`
  margin-bottom: 6px;
`;

const LegendGrid = styled.div<{ $isCompact: boolean }>`
  display: flex;
  flex-direction: ${({ $isCompact }) => ($isCompact ? "column" : "row")};
  gap: 8px;
`;

const LegendColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  min-width: 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-size: 13px;
  line-height: 16px;
  white-space: nowrap;
  width: 100%;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  flex-shrink: 0;
`;

export default Legend;
